import { useRef, useEffect } from 'react'
import { Chart } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import {
	CategoryScale,
	LinearScale,
	BarElement,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	TimeScale,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import 'chartjs-adapter-date-fns'
import * as d3 from 'd3';


Chart.register(
	CategoryScale,
	LinearScale,
	BarElement,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	ChartDataLabels,
	TimeScale
)

const ProjectGanttChart = ({ data, toggleVisibilityForGantt }) => {
	
	let chartJSData = []
	data.forEach((record, i) => {
		let object = null;
		// 現在進行中のプロジェクト（終了日が空の場合）
		if(record.end_year === '' || record.end_month === ''){
			const today = new Date();
			const year = today.getFullYear();
			const month = String(today.getMonth() + 1).padStart(2, '0');
			const day = String(today.getDate()).padStart(2, '0');
			const formattedToday = `${year}-${month}-${day}`;

			object = {
				x:{start: record.start_year + '-' + record.start_month.padStart(2, '0') + '-' + '01', end: formattedToday},
				y: i+1,
				projectName: record.project_name,
			}
		} else {
		// 過去のプロジェクト
			object = {
				x:{start: record.start_year + '-' + record.start_month.padStart(2, '0') + '-' + '01', end: record.end_year + '-' + record.end_month.padStart(2, '0') + '-' + new Date(record.end_year, record.end_month.padStart(2, '0'), 0).getDate()},
				y: i+1,
				projectName: record.project_name,
			}
		}
		chartJSData.push(object)
	});

	// 終了日を最新としてソート
	let sortedChartJSData = d3.sort(chartJSData, (a, b) => {
		// 終了日で降順ソート
		if (a.x.end !== b.x.end) {
			return d3.descending(a.x.end, b.x.end);
		}
		
		// 終了日が同じ場合、開始日で降順ソート（最新が前に）
		if (a.x.start !== b.x.start) {
			return d3.descending(a.x.start, b.x.start);
		}
		
		// 開始日と終了日が同じ場合、データのインデックスで判断（元データが既にcreated_atでソート済み）
		return d3.ascending(a.y, b.y);
	});
	
	// グラフの開始日を設定
	const minStartDate = d3.min(sortedChartJSData, d => d.x.start);
	
	// グラフのデータ作成
	let finalsortedChartJSData = [];	
	sortedChartJSData.forEach((record, i) => {
		let object = {
			x:[record.x.start, record.x.end],
			y: (sortedChartJSData.length - i).toString(),
			projectName: record.projectName,
		}
		finalsortedChartJSData.push(object)
	});
	
	// バーのクリックイベント
	const chartRef = useRef(null)
	useEffect(() => {
		const chart = chartRef.current

		if (chart) {
			const handleClick = (evt) => {				
				const points = chart.getElementsAtEventForMode(
					evt,
					'nearest',
					{ intersect: true },
					false
				)
				if (points.length) {
					const firstPoint = points[0]
					const datasetIndex = firstPoint.datasetIndex
					const index = firstPoint.index
					const projectData = chart.data.datasets[datasetIndex].data[index]
					
					// スクロール先の要素
					const dataProperty = `[data-project="${projectData.y}_${projectData.projectName}"]`
					const targetElement = document.querySelector(dataProperty)

					if (targetElement) {
						// スムーズスクロール
						setTimeout(() => {
							const targetDOMRect = targetElement.getBoundingClientRect();
							const targetTop = targetDOMRect.top + window.scrollY - 80;
							window.scrollTo({
								top: targetTop,
								behavior: 'smooth'
							});
						}, 300);

						// プロジェクトの表示を切り替える（インデックスは0から始まるため、y-1を使用）
						if (toggleVisibilityForGantt && projectData.y) {
							const projectIndex = parseInt(projectData.y)
							toggleVisibilityForGantt(projectIndex)
						}
					}
				}
			}

			// クリックイベントリスナー登録
			chart.canvas.addEventListener('click', handleClick)

			// クリーンアップ関数
			return () => {
				if (chart && chart.canvas) {
					chart.canvas.removeEventListener('click', handleClick)
				}
			}
		}
	}, [chartRef, toggleVisibilityForGantt])

	// バーの背景色を定義
	const colors = ['#0A2B5F', '#95D2E5', '#C9D2D6']

	const config = {
		type: 'bar', // 棒グラフ形式
		data: {
			datasets: [
				{
					backgroundColor: colors,
					borderColor: colors,
					borderWidth: 1,
					barPercentage: 0.4,
					borderSkipped: false,
					borderRadius: 5,
					data: finalsortedChartJSData,
				},
			],
		},
		options: {
			responsive: true,           // レスポンシブデザイン
			maintainAspectRatio: false, // 縦横比を維持
			indexAxis: 'y',             // グラフを縦軸に
			layout: {
				padding: {
					left: 0,
					right: 0,
					top: 0,
					bottom: 0,
				},
			},
			scales: {
				x: {  // 横軸の設定
					offset: false,       // グラフの両端の余白調整
					position: 'top',     // 目盛りをグラフの上に表示
					min: minStartDate.toString(),// ガントチャートの開始日を定義
					max: new Date(),     // 現在日を最終日として設定
					type: 'time',        // 時間軸
					time: {
						unit: 'month',	      // 月単位
						displayFormats: {     // 目盛りの表示フォーマット
							year: 'yyyy',
							month: 'yyyy/MM',
						},
					},
					// reverse: true,      // 時間軸を反転
					ticks: {
						align: 'center',        // 日付ラベルをバーの中央に配置
						maxRotation: 0,         // ラベルの回転を防止
						autoSkip: true,         // 自動的にラベルをスキップ
						maxTicksLimit: 12,      // 表示する最大目盛り数を制限
					},
					grid: {
						drawTicks: true,        // 横軸に目盛りを表示
						drawOnChartArea: true,  // グラフの領域に目盛りを表示
					},
				},
				y: {  // 縦軸の設定
					beginAtZero: true,          // 縦軸の目盛りを0から始める
					ticks: {
						font: {
							weight: '500',
						},
					},
				},
			},
			elements: {
				bar: {
					borderWidth: 2,
				},
			},
			plugins: {
				legend: {
					display: false, // 凡例
				},
				datalabels: {
					display: false, // バー上のラベルを非表示
				},
				tooltip: {
					callbacks: {
						title: function (context) {
							return context[0].raw.projectName;
						},
						label: function (context) {
							// 期間表示（開始日〜終了日）
							let startDate = '';
							let endDate = '';
							const today = new Date();
							const year = today.getFullYear();
							const month = String(today.getMonth() + 1).padStart(2, '0');
							const day = String(today.getDate()).padStart(2, '0');
							const formattedToday = `${year}-${month}-${day}`;

							if(context.raw.x[1] === formattedToday){
								startDate = new Date(context.raw.x[0]).toLocaleDateString('ja-JP');
								endDate = formattedToday;
							} else {
								startDate = new Date(context.raw.x[0]).toLocaleDateString('ja-JP');
								endDate = new Date(context.raw.x[1]).toLocaleDateString('ja-JP');
							}
							return `期間: ${startDate} 〜 ${endDate}`;
						},
					},
					titleFont: {
						size: 16,
						weight: 'bold'
					},
					bodyFont: {
						size: 14
					},
					padding: 10,
					displayColors: false, // 色アイコンを非表示
				},
			},
			onClick: (e, elements) => {
				// このイベントはuseEffectで処理
			},
			onHover: function(evt, elements) {				
				if (elements.length) {					
						document.getElementById('ganttChart').style.cursor = 'pointer';
				} else {
						document.getElementById('ganttChart').style.cursor = 'default';
				}
		}			
			},
		plugins: [ChartDataLabels],
	}

	if (!data) {
		return <div>読み込み中...</div>
	}else{
		return <Bar ref={chartRef} {...config} id="ganttChart"/>
	}
}

export default ProjectGanttChart
